import type { LogFn } from '../../log.js';
import { logImmediate } from '../../log.js';
import { publishFileBrowserActivityToWorker } from '../../files/browser/in-flight.js';
import { CliSqliteInterrupted } from '../sqlite-errors.js';
import {
  logCodeNavCacheLockPresentAtStartup,
  logCodeNavCacheUnavailableFromError,
} from './code-nav-cache-log.js';

type CodeNavCacheInitState = 'idle' | 'pending' | 'ready' | 'unavailable';

let initState: CodeNavCacheInitState = 'idle';
let initPromise: Promise<boolean> | null = null;

export class CodeNavCacheUnavailableError extends Error {
  override readonly name = 'CodeNavCacheUnavailableError';
  constructor() {
    super('Code navigation cache unavailable');
  }
}

export function isCodeNavCacheReady(): boolean {
  return initState === 'ready';
}

export function isCodeNavCacheUnavailable(): boolean {
  return initState === 'unavailable';
}

export function getCodeNavCacheInitPromise(): Promise<boolean> | null {
  return initPromise;
}

/** Kick off code-nav SQLite init without blocking bridge connection setup. */
export function startCodeNavCacheInitialization(log: LogFn = logImmediate): Promise<boolean> {
  if (initState === 'ready') return Promise.resolve(true);
  if (initState === 'unavailable') return Promise.resolve(false);
  if (initPromise != null) return initPromise;

  logCodeNavCacheLockPresentAtStartup(log);

  initState = 'pending';
  initPromise = (async () => {
    try {
      const { ensureCodeNavCacheSqliteInitialized } = await import('./database.js');
      await ensureCodeNavCacheSqliteInitialized();
      initState = 'ready';
      publishFileBrowserActivityToWorker();
      return true;
    } catch (e) {
      if (e instanceof CliSqliteInterrupted) {
        initState = 'idle';
        initPromise = null;
        throw e;
      }
      initState = 'unavailable';
      logCodeNavCacheUnavailableFromError(log, e);
      return false;
    }
  })();
  return initPromise;
}

/** Await background init; returns false when indexing is disabled. */
export async function awaitCodeNavCacheInitialization(): Promise<boolean> {
  if (initState === 'ready') return true;
  if (initState === 'unavailable') return false;
  if (initPromise != null) return initPromise;
  return false;
}

export function resetCodeNavCacheAvailabilityForTests(): void {
  initState = 'idle';
  initPromise = null;
}
